import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Pressable from '@atlaskit/primitives/pressable';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { fg } from '@confluence/feature-gating';

import { getIFSDirectPath } from '../getIFSDirectPath.ts';

const styles = cssMap({
	body: {
		paddingTop: token('space.200'),
		paddingInline: token('space.300'),
		paddingBottom: token('space.300'),
	},
	bodyText: {
		marginBlock: token('space.200'),
		marginInline: 0,
		color: token('color.text.subtlest'),
	},
	buttonGroup: {
		marginTop: token('space.250'),
	},
	frame: {
		position: 'relative',
		display: 'inline-block',
		backgroundColor: token('color.background.brand.bold'),
		cursor: 'pointer',
		textDecoration: 'none',
		borderRadius: token('radius.medium'),
		border: `${token('border.width')} solid ${token('color.border.brand')}`,
		'&:hover': {
			backgroundColor: token('color.background.brand.bold.hovered'),
			borderColor: token('color.border.brand'),
		},
	},
	disabledFrame: {
		pointerEvents: 'none',
		opacity: token('opacity.disabled'),
	},
	primaryButton: {
		pointerEvents: 'none',
		backgroundColor: 'transparent',
		border: 'none',
		color: token('color.text.inverse'),
		position: 'relative',
		fontWeight: token('font.weight.semibold'),
		paddingBlock: 0,
		paddingInline: token('space.250'),
		height: '28px',
		cursor: 'pointer',
	},
});

const i18n = defineMessages({
	oneDriveConfirmModalText: {
		id: 'import-from-sources.import-from-onedrive-confirm-modal.text',
		defaultMessage: 'This site is trying to launch OneDrive. Do you want to continue?',
		description:
			"The text in the modal body which informs users that they're going to be authenticated to OneDrive",
	},
	oneDriveConfirmModalHeader: {
		id: 'import-from-sources.import-from-onedrive-confirm-modal.header',
		defaultMessage: 'Open OneDrive',
		description:
			"The text in the modal header which informs users that they're going to be opening OneDrive",
	},
	launchingOneDriveHeading: {
		id: 'import-from-sources.import-from-onedrive-confirm-modal.sr-heading',
		defaultMessage: 'Launching OneDrive',
		description: 'Screen reader accessible heading for the OneDrive launch modal dialog',
	},
	continueOneDriveConfirmModalButtonLabel: {
		id: 'import-from-sources.import-from-onedrive-confirm-modal.continue',
		defaultMessage: 'Continue',
		description:
			'The text is shown on a primary button in the OneDrive import confirmation modal. Clicking this button confirms the selection and proceeds with importing the document from OneDrive into Confluence.',
	},
});

const ChoreographerModal = ({ children, onClose, testId, width }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width={width} testId={testId}>
			{children}
		</Modal>
	);
};

export const modalId = 'oneDriveConfirmModalId';

type OneDriveConfirmModalTypes = {
	onClose: () => any;
	onIframeLoad: (iframeRef: React.RefObject<HTMLIFrameElement>, modalBackdropNode: Element) => any;
};

export const OneDriveConfirmModal: React.FC<OneDriveConfirmModalTypes> = ({
	onClose,
	onIframeLoad,
}) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [isIframeLoading, setIsIframeLoading] = useState<boolean>(true);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { environment } = useSessionData();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'importOneDriveConfirmModal',
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleIframeLoad = () => {
		setIsIframeLoading(false);
		//haven't found any normal way of changing the styles of the modal's wrapper. have to use the data-testid
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const modalWrapper = document.querySelectorAll(`[data-testid="${modalId}"]`)[0];
		onIframeLoad(iframeRef, modalWrapper);
	};

	const handleClose = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'office365ConfirmModal',
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'office365ConfirmClose',
			},
		}).fire();
		onClose();
	};

	const ifsDirectPath = getIFSDirectPath(environment);
	const isGeminiVr =
		typeof window !== 'undefined' &&
		Boolean((window as typeof window & { Gemini?: boolean }).Gemini);

	return (
		<ChoreographerModal onClose={handleClose} width={360} testId={modalId}>
			<VisuallyHidden>
				<Heading as="h1" size="large">
					<FormattedMessage {...i18n.launchingOneDriveHeading} />
				</Heading>
			</VisuallyHidden>
			<ModalHeader hasCloseButton>
				{fg('platform_dst_modal-dialog-use-modal-title') ? (
					<ModalTitle>
						<FormattedMessage {...i18n.oneDriveConfirmModalHeader} />
					</ModalTitle>
				) : (
					<Heading as="h1" size="medium">
						<FormattedMessage {...i18n.oneDriveConfirmModalHeader} />
					</Heading>
				)}
			</ModalHeader>
			<Box xcss={styles.body}>
				<Box as="p" xcss={styles.bodyText}>
					<FormattedMessage {...i18n.oneDriveConfirmModalText} />
				</Box>
				<Inline alignInline="end" alignBlock="center" space="space.100" xcss={styles.buttonGroup}>
					<Box
						xcss={cx(styles.frame, isIframeLoading && styles.disabledFrame)}
						testId="frame-container"
						aria-disabled={isIframeLoading}
					>
						{/* Skip the outbound picker iframe in Gemini so Continue stays disabled instead of racing onLoad. */}
						{!isGeminiVr && (
							<iframe
								onLoad={handleIframeLoad}
								ref={iframeRef}
								src={ifsDirectPath}
								title="OneDrive picker"
								height="100%"
								width="100%"
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									border: 'none',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									position: 'absolute',
								}}
								aria-hidden="true"
								tabIndex={-1}
							/>
						)}
						<Pressable xcss={styles.primaryButton}>
							<FormattedMessage {...i18n.continueOneDriveConfirmModalButtonLabel} />
						</Pressable>
					</Box>
				</Inline>
			</Box>
		</ChoreographerModal>
	);
};
